import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import TidypaySettings from './TidypaySettings';
import SoftpaySettings from './SoftpaySettings';
import NetsSettings from './NetsSettings';
import VivaWalletSettings from './VivaWalletSettings';

import { usePaymentMethod, useEditPaymentMethodStore } from '../../stores';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../model/PaymentMethodConfig';
import VivaWalletTerminalSettings from './VivaWalletTerminal';
import { useDeletePaymentMethodMutation } from '../../stores/FetcherStores/PaymentMethodFetcher';
import useCompanyId from '../../hooks/useCompanyId';

const ALLOWED_PAYMETHODS = [
  PaymentMethodConfigTypes.TIDYPAY,
  PaymentMethodConfigTypes.SOFTPAY,
  PaymentMethodConfigTypes.NETS_TERMINAL,
  PaymentMethodConfigTypes.VIVA_WALLET,
  PaymentMethodConfigTypes.VIVA_WALLET_TERMINAL,
];

interface PaymentMethodEditProps {
  paymentMethodId?: string;
  open: boolean;
  onClose: () => void;
}

const EditPaymethod = observer(
  ({ paymentMethodId, open, onClose }: PaymentMethodEditProps): JSX.Element => {
    const { paymentMethods, setMessage, vivaMessage } = usePaymentMethod();
    const editPaymentMethodStore = useEditPaymentMethodStore();

    const paymentMethod = paymentMethods?.find((pm) => pm.id === paymentMethodId);

    const [selectedPaymethodType, setSelectedPaymethodType] = useState<string>('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

    const { t } = useTranslation();
    const { mutateAsync: removePaymentMethod } = useDeletePaymentMethodMutation();
    const companyid = useCompanyId();

    useEffect(() => {
      editPaymentMethodStore.setPaymentMethod(paymentMethod);
      setSelectedPaymethodType(paymentMethod?.type || '');
    }, [paymentMethod]);

    useEffect(() => {
      if (!open) {
        setSelectedPaymethodType('');
      }
    }, [open]);

    const handleRemovePayMethod = async (id: string) => {
      await removePaymentMethod(companyid, id)
        .catch((error) => {
          setMessage(error.message, 'error');
        })
        .finally(() => {
          setDeleteDialogOpen(false);
          onClose();
        });
    };

    const handlePaymethodTypeChange = (event: SelectChangeEvent) => {
      const paymethod = event.target.value;
      setSelectedPaymethodType(paymethod);
    };

    const renderPaymethodSettings = () => {
      switch (selectedPaymethodType) {
        case PaymentMethodConfigTypes.TIDYPAY:
          return (
            <TidypaySettings
              onDeletePaymethod={() => setDeleteDialogOpen(true)}
              onClose={onClose}
            />
          );
        case PaymentMethodConfigTypes.SOFTPAY:
          return (
            <SoftpaySettings
              onDeletePaymethod={() => setDeleteDialogOpen(true)}
              onClose={onClose}
            />
          );
        case PaymentMethodConfigTypes.NETS_TERMINAL:
          return (
            <NetsSettings
              onDeletePaymethod={() => setDeleteDialogOpen(true)}
              onClose={onClose}
              paymentMethod={
                paymentMethod?.type === PaymentMethodConfigTypes.NETS_TERMINAL
                  ? paymentMethod
                  : undefined
              }
              paymentMethodId={paymentMethodId}
            />
          );
        case PaymentMethodConfigTypes.VIVA_WALLET:
          return <VivaWalletSettings onDeletePaymethod={() => setDeleteDialogOpen(true)} />;
        case PaymentMethodConfigTypes.VIVA_WALLET_TERMINAL:
          return (
            <VivaWalletTerminalSettings
              onDeletePaymethod={() => setDeleteDialogOpen(true)}
              onClose={onClose}
            />
          );
        default:
          return null;
      }
    };

    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            width: 600,
            height: '100%',
            paddingRight: 4,
            paddingLeft: 4,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              marginTop: 12,
              marginBottom: 2,
              display: 'flex',
              minHeight: 'initial !important',
              flexDirection: 'column',
            }}
          >
            {vivaMessage.text && (
              <Box sx={{ marginBottom: 2, width: '100%' }}>
                <Alert severity={vivaMessage.severity}>{vivaMessage.text}</Alert>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                marginBottom: 2,
              }}
            >
              <Typography variant="h4">
                {editPaymentMethodStore.isNewPaymethod
                  ? t('paymethodScreen.addPaymethod')
                  : t('paymethodScreen.editPaymethod')}
              </Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Toolbar>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            {!editPaymentMethodStore.isNewPaymethod && editPaymentMethodStore.paymentMethod && (
              <Typography variant="h5">
                {DefaultNamesForPaymentMethodConfigTypes[editPaymentMethodStore.paymentMethod.type]}
              </Typography>
            )}
            {editPaymentMethodStore.isNewPaymethod && (
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <InputLabel id="paymethod-label">{t('paymethodScreen.selectPaymethod')}</InputLabel>
                <Select
                  labelId="paymethod-label"
                  id="paymethod-select"
                  value={selectedPaymethodType}
                  defaultValue=""
                  label={t('paymethodScreen.selectPaymethod')}
                  onChange={handlePaymethodTypeChange}
                >
                  {ALLOWED_PAYMETHODS.map((paymethod: PaymentMethodConfigTypes) => (
                    <MenuItem key={paymethod} value={paymethod}>
                      {DefaultNamesForPaymentMethodConfigTypes[paymethod]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {renderPaymethodSettings()}
          </Box>
        </Box>
        {paymentMethodId && (
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {t('paymethodScreen.paymethodWillBeRemoved')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => handleRemovePayMethod(paymentMethodId)}
                color="error"
                variant="contained"
              >
                {t('paymethodScreen.yes')}
              </Button>
              <Button onClick={() => setDeleteDialogOpen(false)} variant="contained" autoFocus>
                {t('paymethodScreen.no')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Drawer>
    );
  },
);

export default EditPaymethod;
